/**
 * The query box for the chosen mode: keyword lines, hashtags, seed accounts,
 * Instagram audio — or, for TikTok sounds, a paste box that resolves each
 * link to a named, checkable chip.
 */
import { useEffect, useState, type JSX } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useSoundLookup } from '../hooks/sounds';
import type { ResolvedSound, SearchModeDef } from '../api/types';
import type { Platform } from '../lib/platform';
import { FieldError } from './controls';
import { SoundLink } from '../components/SoundLink';
import { SuggestedSounds } from './SuggestedSounds';

const textareaClass =
  'min-h-[4.5rem] w-full resize-y rounded-lg border bg-[var(--input-bg)] px-3 py-2 text-sm shadow-control placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25';

export function QueryInput({
  platform,
  mode,
  query,
  sounds,
  onQueryChange,
  onSoundsChange,
  error,
  folderSeeds,
  topicTerms,
}: {
  platform: Platform;
  mode: SearchModeDef;
  query: string;
  sounds: ResolvedSound[];
  onQueryChange: (q: string) => void;
  onSoundsChange: (s: ResolvedSound[]) => void;
  error?: string | null;
  /** Similar-accounts mode: this folder's creators on this platform, to seed from. */
  folderSeeds?: string[];
  /** The form's topic words — TikTok sound mode prefills "Sounds in videos about…" with them. */
  topicTerms?: string[];
}): JSX.Element {
  const id = 'search-query';
  const tiktokSound = mode.id === 'sound' && platform === 'tiktok';
  const hint = tiktokSound
    ? 'Paste a share link (short links work) or an id. Each sound added runs as its own search.'
    : mode.multi
      ? mode.id === 'similar'
        ? 'Handles or profile links, one per line. Each seed is searched on its own.'
        : 'One per line — each runs as its own search'
      : null;

  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground/90">
        {mode.queryLabel}
        {hint && <span className="ml-2 font-normal text-muted-foreground">{hint}</span>}
      </label>
      {tiktokSound ? (
        <SoundPicker id={id} sounds={sounds} onChange={onSoundsChange} invalid={!!error} topicTerms={topicTerms} />
      ) : mode.multi ? (
        <textarea
          id={id}
          aria-invalid={!!error || undefined}
          className={cn(textareaClass, error ? 'border-destructive/70' : 'border-input')}
          placeholder={mode.queryPlaceholder ?? undefined}
          value={query}
          onChange={(ev) => onQueryChange(ev.target.value)}
          rows={3}
        />
      ) : (
        <Input
          id={id}
          aria-invalid={!!error || undefined}
          placeholder={mode.queryPlaceholder ?? undefined}
          value={query}
          onChange={(ev) => onQueryChange(ev.target.value)}
          className={cn(error && 'border-destructive/70')}
        />
      )}
      {mode.id === 'similar' && folderSeeds && folderSeeds.length > 0 && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto justify-self-start px-0"
          onClick={() => onQueryChange(folderSeeds.slice(0, 25).join('\n'))}
        >
          Use this folder's creators ({Math.min(folderSeeds.length, 25)})
        </Button>
      )}
      <FieldError message={error} />
    </div>
  );
}

/**
 * A pasted share link is unusable until it's resolved, and a bare id is
 * unverifiable by eye — so the box takes either and names what it matched.
 * A resolved paste becomes a chip and the box empties for the next one.
 */
function SoundPicker({
  id,
  sounds,
  onChange,
  invalid,
  topicTerms,
}: {
  id: string;
  sounds: ResolvedSound[];
  onChange: (s: ResolvedSound[]) => void;
  invalid: boolean;
  topicTerms?: string[];
}): JSX.Element {
  const [input, setInput] = useState('');
  const lookup = useSoundLookup(input, true);

  useEffect(() => {
    const sound = lookup.sound;
    if (lookup.status !== 'ok' || !sound) return;
    // Pasting the same sound twice is a slip, not a decision worth an error.
    if (!sounds.some((s) => s.musicId === sound.musicId)) onChange([...sounds, sound]);
    setInput('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.status, lookup.sound]);

  return (
    <div className="grid gap-1.5">
      <Input
        id={id}
        aria-invalid={invalid || undefined}
        placeholder="Paste a sound link to add it…"
        value={input}
        onChange={(ev) => setInput(ev.target.value)}
        className={cn(invalid && 'border-destructive/70')}
      />
      {lookup.status === 'loading' && <p className="text-xs text-muted-foreground">Looking up sound…</p>}
      {lookup.status === 'error' && <p className="text-xs text-destructive-strong">{lookup.error}</p>}
      {sounds.length > 0 && (
        <ul className="grid gap-1">
          {sounds.map((sound) => (
            <li key={sound.musicId} className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-1.5">
              {/* A link, because a title alone doesn't settle which sound it is. Chips
                  restored from a saved search carry only the id; the catalogue names them. */}
              <SoundLink
                variant="row"
                platform="tiktok"
                id={sound.musicId}
                title={sound.title}
                author={sound.author}
                coverUrl={sound.coverUrl}
                playUrl={sound.playUrl}
              />
              {sound.title && <Check className="h-4 w-4 shrink-0 text-success-strong" aria-label="Resolved" />}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onChange(sounds.filter((s) => s.musicId !== sound.musicId))}
                aria-label={`Remove ${sound.title ?? sound.musicId}`}
                title="Remove"
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <SuggestedSounds
        sounds={sounds}
        topicTerms={topicTerms}
        onAdd={(sound) => {
          if (!sounds.some((s) => s.musicId === sound.musicId)) onChange([...sounds, sound]);
        }}
      />
    </div>
  );
}
